import { buildArtworkDetailHref } from "@/components/ProjectsInArtwork/utils/artworkListNavigate";
import { buildActiveProjectsDetailHref } from "@/utils/activeProjectsUrlNavigation";
import {
  getStatusConfigEntry,
  normalizeStatusForConfig,
} from "@/utils/helpers/vendorRedirection";

const DESIGN_ARTWORK_PROJECTS_PREFIX = "/design-artwork/projects-in-artwork";

export type StatusConfigPathEntry = {
  redirect_url: string;
  tab?: string | number;
  /**
   * Optional extra URL path prefixes that count as “on the right module” for this status
   * (e.g. Artwork in Design is valid on both Active Projects and Projects in Artwork).
   */
  allowed_path_prefixes?: string[];
};

export type BusinessStatusPathAlignment = {
  /** Status after `normalizeStatusForConfig` (uppercase, underscores → spaces). */
  normalizedStatus: string;
  /** True when `normalizedStatus` has a row in `status-config.json`. */
  hasStatusConfigEntry: boolean;
  /** Entry from `status-config.json` when the normalized status exists. */
  statusConfigEntry: StatusConfigPathEntry | undefined;
  /** Canonical `redirect_url` from config (used for default redirects / home hrefs). */
  expectedModulePathPrefix: string | null;
  /**
   * Path prefixes that satisfy this status (always includes `redirect_url` when set;
   * plus `allowed_path_prefixes` when provided).
   */
  applicablePathPrefixes: string[];
  /**
   * When `hasStatusConfigEntry` is true: pathname (no query) matches at least one of
   * `applicablePathPrefixes`. When false: always true (no config — use `hasStatusConfigEntry`).
   */
  isCurrentPathUnderExpectedModule: boolean;
  /**
   * True when status has a config entry and the current path matches none of the applicable prefixes.
   */
  shouldRedirectToStatusConfiguredHome: boolean;
};

function stripQueryAndHash(path: string): string {
  const s = String(path ?? "").trim();
  const q = s.indexOf("?");
  const h = s.indexOf("#");
  let end = s.length;
  if (q >= 0) end = Math.min(end, q);
  if (h >= 0) end = Math.min(end, h);
  return s.slice(0, end);
}

function normalizePathPrefix(prefix: string): string {
  let p = String(prefix ?? "").trim();
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p;
}

/**
 * True when `pathname` is exactly `redirectPrefix` or starts with `redirectPrefix/` (after normalization).
 */
export function pathnameMatchesStatusRedirectPrefix(
  pathname: string,
  redirectPrefix: string
): boolean {
  const prefix = normalizePathPrefix(redirectPrefix);
  if (!prefix) return false;

  let pathOnly = stripQueryAndHash(pathname);
  if (pathOnly.length > 1) pathOnly = pathOnly.replace(/\/+$/, "");

  if (pathOnly === prefix) return true;
  const withSlash = prefix.endsWith("/") ? prefix : `${prefix}/`;
  return pathOnly.startsWith(withSlash);
}

/**
 * Path prefixes that count as “aligned” for this status-config row: primary `redirect_url`
 * plus any `allowed_path_prefixes` (de-duplicated).
 */
export function getStatusConfigApplicablePathPrefixes(
  entry: StatusConfigPathEntry | null | undefined
): string[] {
  if (!entry) return [];
  const primary = String(entry.redirect_url ?? "").trim();
  const raw = (entry as { allowed_path_prefixes?: unknown }).allowed_path_prefixes;
  const extras = Array.isArray(raw)
    ? raw.map((p) => String(p).trim()).filter((p) => p.length > 0)
    : [];
  if (extras.length === 0) {
    return primary ? [primary] : [];
  }
  return [...new Set([primary, ...extras].filter(Boolean))];
}

function pathnameMatchesAnyApplicablePrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((p) => pathnameMatchesStatusRedirectPrefix(pathname, p));
}

/**
 * Uses normalized business status and `status-config.json` to see whether the current URL path
 * belongs to one of the workflow modules for that status (`redirect_url` and optional
 * `allowed_path_prefixes`).
 *
 * Does not validate query params (e.g. `tab`); use this for “wrong module” redirects. Building the
 * full destination URL (product id, vendor id, etc.) stays with `buildStatusConfigHomeHref`.
 */
export function getBusinessStatusPathAlignment(
  businessStatus: string | null | undefined,
  pathname: string | null | undefined
): BusinessStatusPathAlignment {
  const normalizedStatus = businessStatus
    ? normalizeStatusForConfig(String(businessStatus))
    : "";

  const statusConfigEntry = normalizedStatus
    ? (getStatusConfigEntry(String(businessStatus)) as StatusConfigPathEntry | undefined)
    : undefined;

  const hasStatusConfigEntry = statusConfigEntry != null;

  const applicablePathPrefixes = hasStatusConfigEntry
    ? getStatusConfigApplicablePathPrefixes(statusConfigEntry)
    : [];

  const expectedModulePathPrefix =
    statusConfigEntry?.redirect_url != null &&
    String(statusConfigEntry.redirect_url).trim() !== ""
      ? String(statusConfigEntry.redirect_url).trim()
      : null;

  const isCurrentPathUnderExpectedModule =
    applicablePathPrefixes.length > 0
      ? pathnameMatchesAnyApplicablePrefix(String(pathname ?? ""), applicablePathPrefixes)
      : true;

  const shouldRedirectToStatusConfiguredHome =
    applicablePathPrefixes.length > 0 && !isCurrentPathUnderExpectedModule;

  return {
    normalizedStatus,
    hasStatusConfigEntry,
    statusConfigEntry,
    expectedModulePathPrefix,
    applicablePathPrefixes,
    isCurrentPathUnderExpectedModule,
    shouldRedirectToStatusConfiguredHome,
  };
}

/**
 * Builds the same style of URL as other status-config redirects (`redirect_url/{productId}?…`).
 * Active Projects uses `vendorAssessmentId` + `vendorId` when an assessment id is available.
 * Projects in Artwork uses `/design-artwork/projects-in-artwork/{id}?…` when `redirect_url` targets that module.
 */
export function buildStatusConfigHomeHref(params: {
  statusConfigEntry: StatusConfigPathEntry;
  productId: string | number;
  vendorId: string | number;
  vendorAssessmentId?: string | null;
}): string {
  const { statusConfigEntry, productId, vendorId, vendorAssessmentId } = params;
  const basePath = String(statusConfigEntry.redirect_url ?? "").trim();
  const pid = encodeURIComponent(String(productId));
  const tabRaw = statusConfigEntry.tab;
  const tabStr =
    tabRaw != null && String(tabRaw).trim() !== "" ? String(tabRaw).trim() : null;
  const aid =
    vendorAssessmentId != null && String(vendorAssessmentId).trim() !== ""
      ? String(vendorAssessmentId).trim()
      : "";
  const vid = String(vendorId ?? "").trim();

  const isActiveProjectsModule =
    basePath === "/active-projects" || basePath.startsWith("/active-projects/");

  if (isActiveProjectsModule) {
    if (aid && vid) {
      return buildActiveProjectsDetailHref(productId, vid, aid, tabStr);
    }
    const url = `/active-projects/${pid}`;
    const q = new URLSearchParams();
    if (vid) q.set("vendorId", vid);
    if (tabStr) q.set("tab", tabStr);
    const qs = q.toString();
    return qs ? `${url}?${qs}` : url;
  }

  const isDesignArtworkProjectsModule =
    basePath === DESIGN_ARTWORK_PROJECTS_PREFIX ||
    basePath.startsWith(`${DESIGN_ARTWORK_PROJECTS_PREFIX}/`);

  if (isDesignArtworkProjectsModule) {
    return buildArtworkDetailHref(productId, vid || null, aid || null);
  }

  const url = `${basePath}/${pid}`;
  const q = new URLSearchParams();
  if (vid) q.set("vendorId", vid);
  if (tabStr) q.set("tab", tabStr);
  const qs = q.toString();
  return qs ? `${url}?${qs}` : url;
}
